'use client';

import { useEffect, useRef, useState } from 'react';
import { HStack, Box } from '@terra/ui-styled-system';
import { Button } from '../../components/button';
import { Text } from '../../components/text';
import { Tooltip } from '../../components/tooltip';
import { IconButton } from '../../components/icon-button';
import { Menu } from '../../components/menu';
import { getPocScriptActions } from './poc-script-actions';
import type { InternalEnrichedFinding } from '../../types';
import {
  Tick01Icon,
  ArrowDown01Icon,
  InformationCircleIcon,
  Loading03Icon,
} from '@terra/ui-icons';
import {
  POC_SCRIPT_ACTION_KEYS,
  POC_SCRIPT_DROPDOWN_ORDER,
  PocActionsMode,
  type PocScriptActionKey,
} from './poc-actions-mode';
import { TechnologyIcon } from '../technology-icon';

type FindingPocScriptActionsBarProps = {
  finding: Pick<
    InternalEnrichedFinding,
    'pocScript' | 'pocScriptBlockedByGuardrails' | 'pocScriptGuardrailsFeedback'
  >;
  t: (key: string) => string;
  onGeneratePocScript?: () => void;
  onRegeneratePocScript?: () => void;
  onDownloadPocScript?: () => void;
  onDeletePocScript?: () => void;
  isGeneratingPocScript: boolean;
  isDeletingPocScript?: boolean;
  pocActionsMode?: PocActionsMode;
  mutationsDisabled?: boolean;
  mutationsDisabledTooltip?: string;
};

export function FindingPocScriptActionsBar({
  finding,
  t,
  onGeneratePocScript,
  onRegeneratePocScript,
  onDownloadPocScript,
  onDeletePocScript,
  isGeneratingPocScript,
  isDeletingPocScript = false,
  pocActionsMode = PocActionsMode.Full,
  mutationsDisabled,
  mutationsDisabledTooltip,
}: FindingPocScriptActionsBarProps) {
  const [showGenerateSuccess, setShowGenerateSuccess] = useState(false);
  const previousGeneratingRef = useRef(isGeneratingPocScript);

  useEffect(() => {
    const wasGenerating = previousGeneratingRef.current;

    previousGeneratingRef.current = isGeneratingPocScript;
    if (wasGenerating && !isGeneratingPocScript && finding.pocScript) {
      setShowGenerateSuccess(true);
      const id = setTimeout(() => setShowGenerateSuccess(false), 1500);

      return () => clearTimeout(id);
    }
  }, [isGeneratingPocScript, finding.pocScript]);
  const actions = getPocScriptActions({
    finding,
    t,
    onGeneratePocScript,
    onRegeneratePocScript,
    onDownloadPocScript,
    onDeletePocScript,
    isGeneratingPocScript,
    isDeletingPocScript,
    pocActionsMode,
    mutationsDisabled,
    mutationsDisabledTooltip,
  });
  const guardrailsReason = (finding.pocScriptGuardrailsFeedback ?? '').trim();
  const showBlockedTooltip = Boolean(
    (pocActionsMode === PocActionsMode.Full ||
      pocActionsMode === PocActionsMode.Dropdown) &&
      finding.pocScriptBlockedByGuardrails &&
      guardrailsReason.length > 0,
  );

  if (actions.length === 0 && !showBlockedTooltip) return null;
  const isDropdown = pocActionsMode === PocActionsMode.Dropdown;
  const orderIndex = (actionType: PocScriptActionKey): number => {
    const idx = POC_SCRIPT_DROPDOWN_ORDER.indexOf(actionType);

    return idx === -1 ? POC_SCRIPT_DROPDOWN_ORDER.length : idx;
  };

  const noScriptRetryActionTypes: PocScriptActionKey[] = [
    POC_SCRIPT_ACTION_KEYS.Generate,
    POC_SCRIPT_ACTION_KEYS.Regenerate,
    POC_SCRIPT_ACTION_KEYS.Delete,
  ];
  const dropdownActions = isDropdown
    ? [...actions]
        .filter(
          (
            action,
          ): action is typeof action & {
            actionType: PocScriptActionKey;
          } =>
            action.actionType !== undefined &&
            (finding.pocScript
              ? action.actionType !== POC_SCRIPT_ACTION_KEYS.Generate
              : noScriptRetryActionTypes.includes(action.actionType)),
        )
        .sort((a, b) => orderIndex(a.actionType) - orderIndex(b.actionType))
    : actions;
  const dropdownAriaLabel = finding.pocScript
    ? t(POC_SCRIPT_ACTION_KEYS.Regenerate)
    : t(POC_SCRIPT_ACTION_KEYS.Generate);

  if (isDropdown && !finding.pocScript) {
    const generateAction = dropdownActions[0];
    const generateButtonDisabled =
      isGeneratingPocScript || Boolean(generateAction?.disabled);
    const generateButtonTooltip =
      generateAction?.tooltip ||
      (showBlockedTooltip
        ? guardrailsReason
        : (generateAction?.label ?? dropdownAriaLabel));
    const generateButtonIcon = isGeneratingPocScript ? (
      <Box display="inline-flex" animation="spin 1s linear infinite">
        <Loading03Icon size={14} />
      </Box>
    ) : showGenerateSuccess ? (
      <Box color="fg.success" display="inline-flex">
        <Tick01Icon size={14} />
      </Box>
    ) : (
      <TechnologyIcon name="python" size={14} />
    );
    const generateButton = (
      <IconButton
        variant="ghost"
        size="xs"
        aria-label={generateButtonTooltip}
        disabled={generateButtonDisabled}
        onClick={
          generateAction && !generateAction.disabled
            ? generateAction.onClick
            : undefined
        }
      >
        {generateButtonIcon}
      </IconButton>
    );

    return (
      <HStack gap="2" flexWrap="wrap" alignItems="center">
        <Tooltip
          title={generateButtonTooltip}
          positioning={{ strategy: 'fixed' }}
          maxW={showBlockedTooltip ? '320px' : undefined}
        >
          {generateButton}
        </Tooltip>
      </HStack>
    );
  }

  if (isDropdown && finding.pocScript) {
    const dropdownButtonDisabled: boolean =
      isGeneratingPocScript || showBlockedTooltip;
    const dropdownButtonTooltip = showBlockedTooltip
      ? guardrailsReason
      : undefined;
    const showDropdownTooltip = dropdownButtonTooltip !== undefined;
    const dropdownButton = (
      <IconButton
        variant="ghost"
        size="xs"
        aria-label={dropdownButtonTooltip ?? dropdownAriaLabel}
        disabled={dropdownButtonDisabled}
        loading={isGeneratingPocScript}
      >
        <HStack gap="1" alignItems="center">
          <TechnologyIcon name="python" size={14} />
          <ArrowDown01Icon size={12} />
        </HStack>
      </IconButton>
    );

    return (
      <HStack gap="2" flexWrap="wrap" alignItems="center">
        <Menu.Root
          lazyMount
          unmountOnExit
          positioning={{ strategy: 'fixed', placement: 'bottom-start' }}
        >
          <Menu.Trigger asChild>
            <Box
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
            >
              {showDropdownTooltip ? (
                <Tooltip
                  title={dropdownButtonTooltip}
                  positioning={{ strategy: 'fixed' }}
                  maxW="320px"
                >
                  {dropdownButton}
                </Tooltip>
              ) : (
                dropdownButton
              )}
            </Box>
          </Menu.Trigger>
          <Menu.Positioner>
            <Menu.Content p="0" gap="0" w="auto" minW="180px">
              <Menu.ItemGroup p="1">
                {dropdownActions.map((action, index) => {
                  const Icon = action.icon;

                  return (
                    <Menu.Item
                      key={index}
                      value={String(index)}
                      disabled={action.disabled}
                      title={action.tooltip}
                      display="flex"
                      alignItems="center"
                      gap="2"
                      px="3"
                      py="2"
                      cursor={action.disabled ? 'not-allowed' : 'pointer'}
                      color={
                        action.disabled ? 'fg.neutral.disabled' : 'fg.default'
                      }
                      _hover={{
                        bg: action.disabled
                          ? 'transparent'
                          : 'bg.neutral.hover',
                      }}
                      onClick={() => {
                        if (!action.disabled) {
                          action.onClick();
                        }
                      }}
                    >
                      {Icon && <Icon size={14} />}
                      <Box flex="1" minW="0" textAlign="start">
                        <Text fontSize="sm">{action.label}</Text>
                      </Box>
                    </Menu.Item>
                  );
                })}
              </Menu.ItemGroup>
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>
      </HStack>
    );
  }

  const downloadOnlyTooltip =
    pocActionsMode === PocActionsMode.DownloadOnly
      ? (t('downloadPocScriptTooltip') ?? '').trim()
      : '';
  const showDownloadOnlyTooltip =
    downloadOnlyTooltip.length > 0 && actions.length === 1;

  return (
    <HStack gap="2" flexWrap="wrap" alignItems="center">
      {showBlockedTooltip && (
        <Tooltip
          title={guardrailsReason}
          positioning={{ strategy: 'fixed' }}
          maxW="320px"
        >
          <IconButton variant="ghost" size="xs" aria-label={guardrailsReason}>
            <InformationCircleIcon size={14} />
          </IconButton>
        </Tooltip>
      )}
      {actions.map((action, index) => {
        const Icon = action.icon;
        const button = (
          <Button
            key={index}
            variant="ghost"
            size="xs"
            onClick={action.onClick}
            loading={action.loading}
            disabled={action.disabled}
            colorScheme={action.colorScheme}
          >
            {Icon && <Icon size={14} />}
            <Text ml="1">{action.label}</Text>
          </Button>
        );

        if (showDownloadOnlyTooltip && index === 0) {
          return (
            <Tooltip
              key={index}
              title={downloadOnlyTooltip}
              positioning={{ strategy: 'fixed' }}
              maxW="320px"
            >
              {button}
            </Tooltip>
          );
        }

        return button;
      })}
    </HStack>
  );
}
